import React from "react";
import { useUser } from "util/UserContext";
import Modal from "ui/Modal";
import { Drawer, DrawerContent } from "ui/Drawer";
import { SurfaceLayerName } from "ui/layers";

/**
 * Props for ModalOrDrawer - renders Modal on desktop, Drawer on mobile.
 */
interface ModalOrDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  stickyFooter?: boolean;
  className?: string;
  overlayClassName?: string;
  disableOutsideClose?: boolean;
  layerBase?: number;
  surfaceTier?: SurfaceLayerName;
  /**
   * Optional id of the `<form>` element rendered inside `children`.
   * Forwarded to the underlying Modal/Drawer so footer submit buttons
   * can pair via `<button form={formId} type="submit">`. See
   * `docs/architecture/forms-standard.md` for the contract.
   */
  formId?: string;
}

/**
 * Renders Modal on desktop and Drawer on mobile for consistent add/edit surfaces.
 */
const ModalOrDrawer: React.FC<ModalOrDrawerProps> = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  stickyFooter = true,
  className = "",
  overlayClassName = "",
  disableOutsideClose = false,
  layerBase,
  surfaceTier = "modal",
  formId,
}) => {
  const { userIsMobile } = useUser();

  if (userIsMobile) {
    return (
      <Drawer open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DrawerContent
          title={title}
          footer={footer}
          stickyFooter={stickyFooter}
          className={className}
          layerBase={layerBase}
          formId={formId}
        >
          {children}
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      footer={footer}
      stickyFooter={stickyFooter}
      overlayClassName={overlayClassName}
      disableOutsideClose={disableOutsideClose}
      layerBase={layerBase}
      surfaceTier={surfaceTier}
      formId={formId}
    >
      <div className={className}>{children}</div>
    </Modal>
  );
};

export default ModalOrDrawer;
